import {
  ChangeDetectionStrategy,
  Component,
  HostListener,
  Inject,
  OnDestroy,
  OnInit,
  signal,
  WritableSignal
} from '@angular/core';
import {
  CardComponent,
  ConfirmationModalService,
  confirmationModalServiceInjectionToken,
  EFieldType,
  EPageLayout,
  ESeverity,
  ETabNavigationMode,
  IConfirmationModalService,
  IInputGroupOptions,
  InputGroupComponent,
  ITab,
  IToastService,
  ModalService,
  modalServiceInjectionToken,
  PageLayoutComponent,
  TabNavigationComponent,
  TabNavigationStateService,
  tabNavigationStateServiceInjectionToken,
  toastServiceInjectionToken
} from "@zaeper/angular-dashboard-view-lib";
import { TableLazyLoadEvent, TableModule } from "primeng/table";
import { SkeletonModule } from "primeng/skeleton";
import { DialogService } from 'primeng/dynamicdialog';
import { FormBuilder, FormControl, FormGroup, Validators } from '@angular/forms';
import { ButtonModule } from "primeng/button";
import { Subscription } from 'rxjs';
import { NgClass, NgOptimizedImage } from "@angular/common";
import { ApplicationOverviewItemComponent } from "../../components/application-overview-item";
import { ActivatedRoute, Data, Router, RouterLink } from "@angular/router";
import { ICanComponentDeactivate } from "../../routing/guards/page/i-can-component-deactivate";
import { SanitizeHtmlPipe, ZTemplateDirective } from '@zaeper/angular-core-lib';
import { launcherRestClientInjectionToken } from "../../rest-clients/application/launcher/launcher-rest-client-injection-token";
import { ILauncherRestClient } from "../../rest-clients/application/launcher/i-launcher-rest.client";
import { ILauncher } from "../../models/application/i-launcher";
import { IImageUploadResponse, IPage } from '@zaeper/communication-lib';
import { ILauncherVersion } from "../../models/application-version/i-launcher-version";

@Component( {
  selector: 'launcher-detail-page',
  templateUrl: 'launcher-detail-page.component.html',
  imports: [
    PageLayoutComponent,
    ZTemplateDirective,
    InputGroupComponent,
    ButtonModule,
    SkeletonModule,
    TableModule,
    NgClass,
    TabNavigationComponent,
    ApplicationOverviewItemComponent,
    CardComponent,
    NgOptimizedImage,
    SanitizeHtmlPipe,
    RouterLink
  ],
  providers: [
    {
      provide: modalServiceInjectionToken,
      useClass: ModalService
    }, {
      provide: tabNavigationStateServiceInjectionToken,
      useClass: TabNavigationStateService
    }, {
      provide: confirmationModalServiceInjectionToken,
      useClass: ConfirmationModalService
    }, DialogService
  ],
  changeDetection: ChangeDetectionStrategy.OnPush
} )
export class LauncherDetailPageComponent implements OnInit, OnDestroy, ICanComponentDeactivate {
  protected readonly ePageLayout = EPageLayout;
  protected readonly propertiesInputGroup: IInputGroupOptions;
  protected readonly localizedInfoInputGroup: IInputGroupOptions;
  protected readonly accessControlInputGroup: IInputGroupOptions;
  protected readonly detailTabs: ITab[];
  protected readonly launcherForm: FormGroup;
  protected readonly ETabNavigationMode = ETabNavigationMode;
  protected launcher$: WritableSignal<ILauncher | undefined> = signal( undefined );
  protected launcherVersions$: WritableSignal<IPage<ILauncherVersion> | undefined> = signal( undefined );
  private readonly _subscriptions: Subscription;

  constructor(
    @Inject( toastServiceInjectionToken ) private readonly _toastService: IToastService,
    @Inject( launcherRestClientInjectionToken ) private readonly _launcherRestClient: ILauncherRestClient,
    @Inject(
      confirmationModalServiceInjectionToken ) private readonly _confirmationModalService: IConfirmationModalService,
    private readonly _formBuilder: FormBuilder,
    private readonly _activatedRoute: ActivatedRoute,
    private readonly _router: Router
  ) {
    this.launcherForm = this._formBuilder.group( {
      name: new FormControl( "" ),
      identifier: new FormControl( "", {
        validators: [
          Validators.minLength( 2 ),
          Validators.maxLength( 63 ),
          Validators.pattern( /^[a-z0-9]+(-[a-z0-9]+)*$/ )
        ]
      } ),
      systemApp: new FormControl( false ),
      description: new FormControl( "" )
    } );

    this.detailTabs = this._createDetailTabs();
    this.localizedInfoInputGroup = this._createLocalizedInfoInputGroup();
    this.propertiesInputGroup = this._createPropertiesInputGroup();
    this.accessControlInputGroup = this._createAccessControlInputGroup();
    this._subscriptions = new Subscription();
  }

  canDeactivate(): boolean {
    if ( !this.launcherForm.dirty ) return true;
    return confirm( "Deine Änderungen werden nicht gespeichert. Seite trotzdem verlassen?" );
  };

  ngOnInit() {
    this._subscriptions.add( this._activatedRoute.data.subscribe( ( {
                                                                      launcher,
                                                                      launcherVersions
                                                                    }: Data ) => {
      this.launcher$.set( launcher );
      this.launcherVersions$.set( launcherVersions );

      this.launcherForm.patchValue( {
        name: launcher.name,
        identifier: launcher.identifier ?? "",
        systemApp: launcher.systemApp ?? false,
        description: launcher.description
      } );
    } ) );

    this._listenToLauncherFormChanges();
  }

  ngOnDestroy(): void {
    this._subscriptions.unsubscribe();
  }

  @HostListener( 'window:beforeunload', [ '$event' ] ) handleBeforeUnload( event: BeforeUnloadEvent ): void {
    if ( this.launcherForm.dirty ) {
      event.preventDefault();
    }
  }

  public lazyLoadTable( tableLazyLoadEvent: TableLazyLoadEvent ): Promise<void> {
    return Promise.resolve();
  }

  protected async discardChanges(): Promise<void> {
    const shouldDiscardChanges: boolean = confirm( "Entwurf löschen?" );
    if ( shouldDiscardChanges ) {
      this.launcherForm.reset();
      await this._router.navigate( [ "de", "applications" ] );
    }
  }

  protected async uploadTitleImage( event: Event ): Promise<void> {
    if ( this.launcher$() === undefined ) return;

    const htmlInputElement: HTMLInputElement = <HTMLInputElement>event.target;

    if ( htmlInputElement.files && htmlInputElement.files.length ) {
      const file: File = htmlInputElement.files[ 0 ];

      try {
        const publicImageUrl: IImageUploadResponse = await this._launcherRestClient.uploadTitleImage( this.launcher$()!.id, {
          titleImage: file
        } );

        this.launcher$.update( ( launcher: ILauncher | undefined ): ILauncher => {
          return {
            ...launcher,
            titleImageUrl: publicImageUrl.imageUrl
          } as ILauncher;
        } );

        this._toastService.setMessage( {
          title: "Erfolgreich",
          detail: "Titelbild wurde erfolgreich hochgeladen.",
          severity: ESeverity.success
        } );
      } catch ( e ) {
        this._toastService.setMessage( {
          title: "Fehlgeschlagen",
          detail: "Titelbild konnte nicht hochgeladen werden.",
          severity: ESeverity.error
        } );
      }
    }
  }

  protected async uploadIconImage( event: Event, iconType: "light" | "dark" ): Promise<void> {
    if ( this.launcher$() === undefined ) return;

    const htmlInputElement: HTMLInputElement = <HTMLInputElement>event.target;

    if ( htmlInputElement.files && htmlInputElement.files.length ) {
      const file: File = htmlInputElement.files[ 0 ];

      try {
        const publicImageUrl: IImageUploadResponse = await this._launcherRestClient.uploadIconImage( this.launcher$()!.id, {
          iconImage: file,
          iconType
        } );

        this.launcher$.update( ( launcher: ILauncher | undefined ): ILauncher => {
          if ( iconType === "light" ) {
            return {
              ...launcher,
              lightIconUrl: publicImageUrl.imageUrl
            } as ILauncher;
          } else {
            return {
              ...launcher,
              darkIconUrl: publicImageUrl.imageUrl
            } as ILauncher;
          }
        } );

        this._toastService.setMessage( {
          title: "Erfolgreich",
          detail: "Launcher Icon wurde erfolgreich hochgeladen.",
          severity: ESeverity.success
        } );
      } catch ( e ) {
        this._toastService.setMessage( {
          title: "Fehlgeschlagen",
          detail: "Launcher Icon konnte nicht hochgeladen werden.",
          severity: ESeverity.error
        } );
      }
    }
  }

  protected async update(): Promise<void> {
    const launcher: ILauncher | undefined = this.launcher$();

    if ( launcher === undefined ) return;

    try {
      await this._launcherRestClient.updateApplication( launcher.id, this.launcherForm.value );
      this.launcherForm.reset( this.launcherForm.getRawValue() );

      this._toastService.setMessage( {
        title: "Erfolgreich",
        detail: "Launcher wurde erfolgreich aktualisiert",
        severity: ESeverity.success
      } );
    } catch ( e ) {
      this._toastService.setMessage( {
        title: "Fehlgeschlagen",
        detail: "Launcher konnte nicht aktualisiert werden",
        severity: ESeverity.error
      } );
    }
  }

  protected async publish(): Promise<void> {
    const launcher: ILauncher | undefined = this.launcher$();

    if ( launcher === undefined ) return;

    try {
      await this._launcherRestClient.publishApplication( launcher.id );

      this._toastService.setMessage( {
        title: "Erfolgreich",
        detail: "Launcher wurde erfolgreich veröffentlicht",
        severity: ESeverity.success
      } );
    } catch ( e ) {
      this._toastService.setMessage( {
        title: "Fehlgeschlagen",
        detail: "Launcher konnte nicht veröffentlicht werden",
        severity: ESeverity.error
      } );
    }
  }

  private _listenToLauncherFormChanges() {
    this._subscriptions.add( this.launcherForm.valueChanges.subscribe( ( form ) => {
      this.launcher$.set( {
        ...this.launcher$()!,
        name: form.name,
        identifier: form.identifier || undefined,
        systemApp: !!form.systemApp,
        description: form.description
      } );
    } ) );
  }

  private _createDetailTabs(): ITab[] {
    return [
      {
        subject: 'English',
        title: 'English'
      }
    ];
  }

  private _createLocalizedInfoInputGroup(): IInputGroupOptions {
    return {
      groupId: "localized-info",
      children: [
        {
          fieldType: EFieldType.TEXTAREA,
          title: 'Beschreibung',
          inputId: 'description',
          placeholder: 'Beschreibung',
          required: false,
          formControlEntry: {
            formControl: this.launcherForm.get( "description" ) as FormControl<string | null>,
            key: "description"
          },
          wysiwygEditor: false
        }
      ]
    };
  }

  private _createPropertiesInputGroup(): IInputGroupOptions {
    return {
      groupId: "properties",
      title: "Eigenschaften",
      children: [
        {
          fieldType: EFieldType.TEXT,
          placeholder: "Name",
          required: true,
          formControlEntry: {
            formControl: this.launcherForm.controls[ "name" ] as FormControl<string | null>,
            key: "name"
          },
          title: "Name",
          inputId: "name"
        }, {
          fieldType: EFieldType.TEXT,
          placeholder: "e.g. my-launcher",
          required: false,
          formControlEntry: {
            formControl: this.launcherForm.controls[ "identifier" ] as FormControl<string | null>,
            key: "identifier"
          },
          title: "Identifier",
          description: "URL-safe slug used in the deployment path. Lowercase alphanumeric and hyphens only.",
          inputId: "identifier"
        }, {
          fieldType: EFieldType.BOOLEAN,
          placeholder: "System app",
          required: false,
          formControlEntry: {
            formControl: this.launcherForm.controls[ "systemApp" ] as FormControl<boolean | null>,
            key: "systemApp"
          },
          title: "System app",
          description: "Marks this as a Zaeper-built system app (vs. third-party). Affects the deployment URL prefix.",
          inputId: "system-app"
        }
      ]
    };
  }

  private _createAccessControlInputGroup(): IInputGroupOptions {
    return {
      groupId: "access-control",
      title: "Freigabeeinstellungen",
      children: [
        {
          fieldType: EFieldType.BOOLEAN,
          placeholder: "Öffentlich",
          required: true,
          formControlEntry: {
            formControl: new FormControl( true ),
            key: "is-public"
          },
          title: "Öffentlich",
          description: "Diese Einstellung legt fest, ob Ihr Launcher für alle im öffentlichen Store sichtbar ist oder nur für Sie bzw. ausgewählte Personen in der privaten Repository zugänglich sind.",
          inputId: "is-public"
        }
      ]
    };
  }
}
